<template>
  <div
    id="previewContainer"
    :class="getIsSmallLaptop ? 'unrestrictedHeight' : undefined"
  >
    <page class="previewControls">
      <template slot="content">
        <div
          v-if="
            defaultControls == null || defaultControls.includes('fake-date')
          "
          class="datetime-pickers"
        >
          <label>{{ $tc('PREVIEW.DATE') }}</label>
          <DateTimePickers
            :value="previewFakeDate.toISOString()"
            @input="setPreviewFakeDate"
          />
        </div>

        <div class="sync-control">
          <label>{{ $tc('PREVIEW.NAVIGATION') }}</label>
          <button class="btn btn-secondary btn-sm" @click="resyncPreview">
            <i class="fas fa-location-arrow" />
            {{ $tc('PREVIEW.RESYNC', 1, { page: resyncTargetName }) }}
          </button>
        </div>

        <slot name="controls" />
      </template>
    </page>
    <ThreePositionSwitch
      leftLabel="XS"
      centerLabel="Medium"
      rightLabel="Large"
      @change="setAspectRatio"
    />
    <div class="platform" :style="{ height: platformHeight }">
      <div id="wrapper" class="light preview_BackGround">
        <web-mobile-preview
          v-if="previewEverHadData"
          id="preview"
          ref="mobilePreview"
          :page="JSON.stringify(pageIdentifier)"
          :packed-data="JSON.stringify(packedData)"
          :environment="JSON.stringify(mobilePreviewEnvironment)"
          :fake-time="previewFakeDate.unix() * 1000"
          :style="'background-color: ' + backgroundColor"
          @page-change="onPageChange"
        />
        <slot v-if="!previewEverHadData && showContent" name="content" />
        <div v-if="!previewEverHadData && !showContent" id="loading-screen">
          <span>{{ $tc('PREVIEW.STILL_LOADING') }}</span>
        </div>
      </div>
      <div class="phone-clock">{{ previewFakeDate.format('HH:mm') }}</div>
      <div class="phone-frame">
        <img id="phone-top" src="@/assets/phone_frame_top.webp" />
        <img id="phone-middle" src="@/assets/phone_frame_center.webp" />
        <img id="phone-bottom" src="@/assets/phone_frame_bottom.webp" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import '@code-troopers/mobile-preview'

import dayjs from 'dayjs'
import { defineComponent, type PropType } from 'vue'

import DateTimePickers from '@/pages/festival/components/DateTimePickers.vue'
import {
  DeeplinkElementType,
  isDeeplinkElementType,
} from '@/pages/menuEntries/MenuEntry.type'
import { ExternalScriptsService } from '@/shared/service/externalScriptsService'
import { FontsService } from '@/shared/service/fontsService'
import { colorService } from '@/shared/service/utils.service'
import { type PreviewPageData } from '@/shared/store/preview.store'
import { type PreviewGrabData } from '@/shared/types/applicationData.type'

import { exhaustiveCheck } from '../../../utils'
import { mapGettersIsMobile } from '../../store/isMobile.store'
import ThreePositionSwitch, {
  type SwitchPosition,
} from '../ThreePositionSwitch.vue'

export default defineComponent({
  components: {
    ThreePositionSwitch,
    DateTimePickers,
  },
  props: {
    page: {
      type: String as PropType<DeeplinkElementType>,
      required: true,
      validator: (v: string) => isDeeplinkElementType(v),
    },
    showContent: {
      type: Boolean,
      required: false,
      default: false,
    },
    pageData: {
      type: Object as PropType<PreviewPageData | null>,
      required: false,
      default: null,
    },
    packedData: {
      type: Object as PropType<PreviewGrabData | null>,
      required: false,
      default: null,
    },
    defaultControls: {
      type: Array as PropType<string[]>,
      required: false,
    },
  },
  data: () => {
    return {
      mobilePreviewEnvironment:
        ExternalScriptsService.getMobilePreviewEnvironment(),
      previewFakeDate: dayjs(),
      aspectRatio: 9 / 16,
      previewEverHadData: false,
    }
  },
  computed: {
    ...mapGettersIsMobile(['getIsSmallLaptop']),

    platformHeight(): string {
      const WIDTH_REM = 25
      return `${WIDTH_REM / this.aspectRatio}rem`
    },

    pageIdentifier() {
      return {
        ...this.pageData,
        params: this.pageData,
        type: this.page,
      }
    },

    getAppHeaderColor(): string {
      return colorService.argb_to_rgba(
        this.packedData?.applicationConfig.colorTitle ?? '',
      )
    },

    backgroundColor(): string {
      return colorService.argb_to_rgba(
        this.packedData?.applicationConfig.colorBackground ?? '',
      )
    },

    resyncTargetName(): string {
      const { resyncToHome, resyncPage, page } = this.$store.state.preview
      if (resyncToHome)
        return this.$t('DEEPLINK_COMPONENT.LABEL.HOME') as string

      const name = resyncPage || page || 'HOME'

      // Mapping of backoffice page names to mobile app i18n keys
      const mapping: Record<string, string> = {
        [DeeplinkElementType.EVENT]: 'SHOW',
        [DeeplinkElementType.GUEST]: 'SHOWMAN',
        [DeeplinkElementType.PRACTICAL_INFO]: 'INFO',
        [DeeplinkElementType.PRACTICAL_INFO_LIST]: 'PRACTICALINFOS',
        [DeeplinkElementType.SPONSOR_LIST]: 'SPONSORS',
        [DeeplinkElementType.HOME]: 'HOME',
        [DeeplinkElementType.PROGRAM]: 'PROGRAMMATION',
        [DeeplinkElementType.NOTIFICATIONS]: 'NOTIFICATIONS',
        [DeeplinkElementType.LIKES]: 'LIKES',
        [DeeplinkElementType.MENU]: 'MENU',
        [DeeplinkElementType.CHAT]: 'CHAT',
        [DeeplinkElementType.MY_TICKETS]: 'MY_TICKETS',
        [DeeplinkElementType.GUEST_LIST]: 'GUEST_LIST',
        [DeeplinkElementType.GUEST_LIST_TYPE]: 'GUEST_LIST_TYPE',
        [DeeplinkElementType.WEEZEVENT]: 'WEEZEVENT',
        [DeeplinkElementType.SPLASH]: 'SPLASH',
        [DeeplinkElementType.MAP]: 'MAP',
        [DeeplinkElementType.GALLERY]: 'GALLERY',
        [DeeplinkElementType.PROGRAM_TAGS_FILTER]: 'PROGRAM_TAGS_FILTER',
        [DeeplinkElementType.SAFER]: 'SAFER',
        [DeeplinkElementType.VIDEOS]: 'VIDEOS',
        [DeeplinkElementType.ABOUT]: 'ABOUT',
        [DeeplinkElementType.TICKETING]: 'TICKETING',
        [DeeplinkElementType.CASHLESS]: 'CASHLESS',
        [DeeplinkElementType.EMPTY]: 'EMPTY',
      }

      const key = mapping[name] || name.toUpperCase()
      const i18nKey = `DEEPLINK_COMPONENT.LABEL.${key}`

      return this.$te(i18nKey) ? (this.$t(i18nKey) as string) : name
    },
  },
  watch: {
    packedData: {
      immediate: true,
      handler(data: PreviewGrabData | null) {
        if (data && !this.previewEverHadData) {
          this.previewEverHadData = true
        }
      },
    },
  },
  async mounted(): Promise<void> {
    await FontsService.createPreviewStylesFromAppConfig()
  },
  methods: {
    resyncPreview() {
      const mobilePreview = this.$refs.mobilePreview as
        | (HTMLElement & { forceResync: () => void })
        | undefined
      const { resyncToHome, resyncPage, resyncPageData, page, pageData } =
        this.$store.state.preview

      // Determine the target: if resyncToHome is active (pages without sync), go to Home.
      // Otherwise, use the explicit resync target, or fall back to the current page.
      const targetPage = resyncToHome
        ? DeeplinkElementType.HOME
        : resyncPage || page || DeeplinkElementType.HOME
      const targetData = resyncToHome ? null : resyncPageData || pageData

      // Force update the store to ensure attributes are correct
      this.$store.commit('preview/setPreview', {
        page: targetPage,
        pageData: targetData,
      })

      // Wait for Vue to update DOM attributes, then force React to reload
      this.$nextTick(() => {
        if (mobilePreview && typeof mobilePreview.forceResync === 'function') {
          mobilePreview.forceResync()
        }
      })
    },
    setPreviewFakeDate(dateAsISOString: string): void {
      this.previewFakeDate = dayjs(dateAsISOString)
    },
    setAspectRatio(position: SwitchPosition): void {
      switch (position) {
        case 'left':
          this.aspectRatio = 3 / 4
          break
        case 'center':
          this.aspectRatio = 9 / 16
          break
        case 'right':
          this.aspectRatio = 9 / 19.5
          break
        default:
          exhaustiveCheck(position)
      }
    },
    onPageChange(event: Event) {
      if (!(event instanceof CustomEvent)) return
      const { detail } = event
      if (!detail || typeof detail !== 'object') return
      if (!('type' in detail) || typeof detail.type !== 'string') return
      if (!isDeeplinkElementType(detail.type)) return

      const pageData =
        'params' in detail &&
        typeof detail.params === 'object' &&
        detail.params != null
          ? (detail.params as Record<string, unknown>)
          : null

      this.$store.commit('preview/setPreview', {
        page: detail.type,
        pageData,
      })
    },
  },
})
</script>

<style scoped lang="scss">
#previewContainer {
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow: hidden auto;
  flex-grow: 1;
}
#previewContainer:not(.unrestrictedHeight) {
  max-height: 100vh;
}

.previewControls {
  margin: 0;
  max-width: 43vh;

  .datetime-pickers {
    margin-bottom: -1rem;
  }

  .sync-control {
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: -1rem;
  }
}

.platform {
  line-height: 1.5vh;
  position: relative;

  flex-shrink: 0;
  display: flex;
  width: 25rem;
  padding: 0.5rem 0.6rem 0.3rem 0.7rem;
  transform: scale(0.75);
  margin-bottom: -6rem;
  transform-origin: center top;

  #preview {
    display: block;
    height: 100%;
  }

  #wrapper {
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
    width: 100%;
    box-shadow: 0px 0px 32px -5px #000000;
    border-radius: 2.5rem;
    overflow: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      display: none;
    }
  }

  #loading-screen {
    width: 100%;
    height: 100%;
    background-color: #aaaaaa;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .phone-frame {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }

  .phone-clock {
    position: absolute;
    top: 1.8rem;
    font-weight: bold;
    font-size: large;
    left: 3rem;
    pointer-events: none;
  }

  #phone-top,
  #phone-bottom {
    width: 100%;
    height: auto;
  }

  #phone-middle {
    flex-grow: 1;
    height: calc(100% - 1900px);
    width: 100%;
  }
}

web-mobile-preview {
  height: 100%;
}
</style>
<style>
web-mobile-preview > div > div > div > div {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

web-mobile-preview > div > div > div > div::-webkit-scrollbar {
  display: none;
}
</style>
